import React, { useEffect, useState } from 'react';

import { Box, Card, CardActionArea, CardContent, CardHeader, CardMedia, CircularProgress, Container, Grid, Pagination, TextField, Typography } from '@mui/material';
import { Recipe } from '../../services/api/models';
import { Api } from '../../services/api';
import { RecipeSearchParams } from '../../services/api/recipe';
import { Link, useSearchParams } from 'react-router-dom';
import ImageIcon from '@mui/icons-material/Image';
import idx from 'idx';

// Component definition
function SearchRecipePage() {

  const PAGE_SIZE = 12;

  const [searchParams, setSearchParams] = useSearchParams();

  const [query, setQuery] = useState(searchParams.get('name') || '');
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const nameParam = searchParams.get('name');
  const limit = parseInt(searchParams.get('limit') || PAGE_SIZE.toString());

  useEffect(() => {
    setQuery(nameParam || '');
  }, [nameParam]);

  const handlePageChange = (newPage: number) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('page', newPage.toString());
      setPage(newPage);
      return params;
    });
  };

  const handleQueryChange = (name: string) => {
    setSearchParams(prev => {
      const qparams = new URLSearchParams(prev);
      qparams.set('name', name);
      return qparams;
    });
  };

  useEffect(() => {

    // call api or anything
    async function fetchRecipe() {
      const params: RecipeSearchParams = {};

      handleQueryChange(query);

      if (query && query.length > 1) {
        params.name = query;
      }

      if (page > 0) {
        params.offset = limit * (page - 1);
      }

      params.limit = limit;

      Api.Recipe.search(params)
        .then(response => {
          setRecipes(response.data.results);
          setTotalPages(response.data.pagination.pages)
          setLoading(false);
        }).catch(error => {
          console.warn(error);
        });
    }

    fetchRecipe()
 }, [page, query]);

  if (recipes) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
      <TextField
        label="Search Recipes"
        variant="outlined"
        fullWidth
        value={query}
        onChange={(e) => {
          handlePageChange(1);
          setQuery(e.target.value);
        }}
        sx={{ mb: 4 }}
      />

      {loading ? (
        <Grid container justifyContent="center">
          <CircularProgress />
        </Grid>
      ) : recipes.length === 0 ? (
        <Typography variant="h6" align="center">
          🥣 No recipes found. Try a different search!
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {recipes.map((recipe) => (
              <Grid item xs={12} sm={12} md={6} lg={4} xl={3} key={recipe.id} spacing={3}>
                <Card sx={{ height: 365 }}>
                  <CardActionArea component={Link} to={`/recipe/${recipe.id}`}>
                  <CardHeader
                    title={<Typography variant="h3" noWrap>{recipe.name}</Typography>}
                    subheader={<Typography variant="body2" color="text.secondary">By {recipe.user.display_name}</Typography>}
                  />
                    {recipe.images && recipe.images.length? (
                    <CardMedia
                      component="img"
                      height={194}
                      image={'' + idx(recipe, _ => _.images?.[0].url)}
                      alt={recipe.name}
                    />
                    ) : (
                      <CardMedia>
                        <Box
                          height={194}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          bgcolor="#f0f0f0"
                        >
                          <ImageIcon fontSize="large" color="action" />
                        </Box>
                      </CardMedia>
                    )}
                    <CardContent>
                      <Typography variant="body2" color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {recipe.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container justifyContent="center" sx={{ mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => handlePageChange(value)}
              color="primary"
            />
          </Grid>
        </>
      )}
    </Container>
    );
  } else {
    return (
      <Box>
        <Typography variant='h1'>Recipe Not Found</Typography>
      </Box>
    )
  }
}


// Default export
export default SearchRecipePage;
